import React, { useState, useEffect, createContext, useContext } from "react";
import { Outlet, useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Paper,
  Chip,
} from "@mui/material";
import {
  ExitToApp as LogoutIcon,
  History as HistoryIcon,
  ShoppingCart as CartIcon,
  PointOfSale as CashDrawerIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../services/api";

// カート状態を共有するコンテキスト
export const POSCartContext = createContext({
  cartItemCount: 0,
  cartTotal: 0,
  setCartState: () => {},
});

export const usePOSCart = () => useContext(POSCartContext);

const POSLayout = () => {
  const [storeName, setStoreName] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { storeId } = useParams();
  const { user, logout } = useAuth();
  const [sessionOpen, setSessionOpen] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [cartState, setCartState] = useState({ itemCount: 0, total: 0 });

  useEffect(() => {
    const fetchStoreName = async () => {
      try {
        const response = await api.get(`/api/stores/${storeId}`);
        setStoreName(response.data.name);
      } catch (error) {
        console.error("店舗情報の取得に失敗しました:", error);
      }
    };

    fetchStoreName();
    const fetchSession = async () => {
      try {
        const res = await api.get(`/api/sessions/current`, {
          params: { storeId },
        });
        setSessionOpen(!!res.data?.id);
      } catch (e) {
        setSessionOpen(false);
      }
    };
    fetchSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleHistoryClick = () => {
    navigate(`/pos/${storeId}/history`);
  };

  const handleOrderClick = () => {
    navigate(`/pos/${storeId}`);
  };

  const handleCheckoutClick = () => {
    navigate(`/pos/${storeId}/checkout`);
  };

  const handleCashDrawerClick = () => {
    navigate(`/pos/${storeId}/cash-drawer`);
  };

  const handleOpenSession = async () => {
    try {
      setSessionLoading(true);
      await api.post(`/api/sessions/open`, { storeId });
      setSessionOpen(true);
    } catch (e) {
      console.error("営業開始に失敗:", e);
    } finally {
      setSessionLoading(false);
    }
  };

  const handleCloseSession = async () => {
    try {
      setSessionLoading(true);
      await api.post(`/api/sessions/close`, { storeId });
      setSessionOpen(false);
    } catch (e) {
      console.error("営業終了に失敗:", e);
    } finally {
      setSessionLoading(false);
    }
  };

  // 金額フォーマット
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(value);
  };

  return (
    <POSCartContext.Provider value={{ ...cartState, setCartState }}>
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100dvh" }}>
      <AppBar position="fixed">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {storeName} - POS
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {user?.username}
            </Typography>
            {user?.role === "STORE_ADMIN" && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2 }}
              >
                <Typography
                  variant="body2"
                  color={sessionOpen ? "success.light" : "error.light"}
                >
                  {sessionOpen ? "営業中" : "営業時間外"}
                </Typography>
                {sessionOpen ? (
                  <Button
                    color="inherit"
                    variant="outlined"
                    size="small"
                    onClick={handleCloseSession}
                    disabled={sessionLoading}
                    data-testid="btn-close-session"
                  >
                    閉店
                  </Button>
                ) : (
                  <Button
                    color="inherit"
                    variant="outlined"
                    size="small"
                    onClick={handleOpenSession}
                    disabled={sessionLoading}
                    data-testid="btn-open-session"
                  >
                    開店
                  </Button>
                )}
              </Box>
            )}
            <Button
              color="inherit"
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
            >
              ログアウト
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ステータスバー: 現在の注文状態を表示 */}
      {cartState.itemCount > 0 && (
        <Box
          sx={{
            position: "fixed",
            top: 64,
            left: 0,
            right: 0,
            bgcolor: "info.main",
            color: "info.contrastText",
            py: 1,
            px: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            zIndex: 1099,
          }}
        >
          <Chip
            label={`注文中: ${cartState.itemCount}点`}
            color="default"
            sx={{ bgcolor: "white", fontWeight: 600 }}
          />
          <Typography variant="h6" fontWeight={700}>
            {formatCurrency(cartState.total)}
          </Typography>
          <Button
            variant="contained"
            color="warning"
            size="small"
            onClick={handleCheckoutClick}
            sx={{ ml: 2 }}
          >
            会計へ進む
          </Button>
        </Box>
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: cartState.itemCount > 0 ? 14 : 8,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* ナビゲーション: 注文入力を大きく、他は小さく */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button
            variant={
              location.pathname === `/pos/${storeId}`
                ? "contained"
                : "outlined"
            }
            onClick={handleOrderClick}
            startIcon={<CartIcon />}
            sx={{
              height: "56px",
              flex: { xs: '1 1 100%', sm: '2 1 0' },
              fontSize: '1.1rem',
              fontWeight: 700,
            }}
            data-testid="nav-pos"
          >
            注文入力
          </Button>
          <Button
            variant={
              location.pathname.includes("/history")
                ? "contained"
                : "outlined"
            }
            onClick={handleHistoryClick}
            startIcon={<HistoryIcon />}
            size="small"
            sx={{
              height: "56px",
              flex: { xs: '1 1 45%', sm: '1 1 0' },
            }}
          >
            履歴
          </Button>
          <Button
            variant={
              location.pathname.includes("/cash-drawer")
                ? "contained"
                : "outlined"
            }
            onClick={handleCashDrawerClick}
            startIcon={<CashDrawerIcon />}
            color={!sessionOpen ? "warning" : "primary"}
            size="small"
            sx={{
              height: "56px",
              flex: { xs: '1 1 45%', sm: '1 1 0' },
              ...((!sessionOpen) && {
                bgcolor: "warning.light",
                borderColor: "warning.main",
                "&:hover": { bgcolor: "warning.main" }
              })
            }}
          >
            レジ締め
            {!sessionOpen && (
              <WarningIcon sx={{ ml: 0.5, fontSize: 16 }} />
            )}
          </Button>
        </Box>
        <Paper
          sx={{
            flexGrow: 1,
            p: { xs: 0, sm: 2 },
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Outlet />
        </Paper>
      </Box>
    </Box>
    </POSCartContext.Provider>
  );
};

export default POSLayout;
